import { autorun, computed, IComputedValue, runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useEffect, useRef, useState } from "react";
import { atom } from "../atom";
import { ActionType, EnvironmentVariableValue, Pane, PaneType } from "../Types";
import { FeatherIcon } from "../ui/FeatherIcon";
import { SecretValue } from "../ui/SecretValue";
import { assert } from "../Utils";
import { canvasDispatch } from "./CanvasDispatch";
import { activePaneStates } from "./CanvasState";
import { getEnvironmentVariableAtom } from "./EnvironmentVariable";
import { ParentComlinkAPI } from "./FrameSandboxAPI";
import { PaneBody } from "./PaneBody";
import { PaneTransformButton } from "./PaneHeader";
import { PaneHeaderBar } from "./PaneHeaderBar";

function PaneVariableKeyInput({
  paneId,
  paneKey,
  focusOnMount,
  onClose,
}: {
  paneId: string;
  paneKey: string | undefined;
  focusOnMount: boolean;
  onClose: () => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [value, setValue] = useState(() => paneKey ?? "");
  const hasChange = paneKey !== value;
  useEffect(() => {
    if (focusOnMount) {
      inputRef.current!.focus();
    }
  }, []);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (hasChange) {
          canvasDispatch([
            ActionType.SetPaneEnvironmentKey,
            {
              paneId,
              key: value,
            },
          ]);
        }
        onClose();
      }}
      className="flex h-full bg-neutral-50 pr-2"
    >
      <input
        type="text"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
        }}
        placeholder="key, eg MY_API_TOKEN"
        className="grow bg-transparent font-mono-natto px-2 min-w-0 outline-none focus-visible-inset"
        ref={inputRef}
      />
      <button
        disabled={value === "" || !hasChange}
        className="px-1 py-0.5 self-center rounded-sm ml-2 button-dark-color disabled:bg-opacity-50 disabled:hover:bg-opacity-25"
      >
        update
      </button>
      {paneKey !== undefined ? (
        <button
          type="button"
          onClick={() => {
            onClose();
          }}
          className="text-neutral-400 hover:text-neutral-600 transition ml-2"
        >
          cancel
        </button>
      ) : null}
    </form>
  );
}

export class UnauthorizedError extends Error {
  message = "unauthorized";
}

const undefinedVariableAtom = atom<EnvironmentVariableValue>(["loading"]);

export const PaneEnvironmentVariable = observer(
  ({
    paneId,
    paneComputed,
    isMaximized,
    focusOnMount,
  }: {
    paneId: string;
    paneComputed: IComputedValue<
      Extract<Pane, { type: PaneType.EnvironmentVariable }> | undefined
    >;
    isMaximized: boolean;
    focusOnMount: boolean;
  }) => {
    const activePaneState = activePaneStates.get(paneId)!;
    assert(activePaneState.type === PaneType.EnvironmentVariable);
    const outputBox = activePaneState.outputBoxes[0];
    const output = outputBox.get();
    const paneKey = computed(() => paneComputed.get()?.key).get();
    const variableAtom =
      paneKey !== undefined
        ? getEnvironmentVariableAtom(paneKey)
        : undefinedVariableAtom;
    useEffect(
      () =>
        autorun(() => {
          switch (variableAtom.value[0]) {
            case "loading":
              runInAction(() => {
                outputBox.set(["running"]);
              });
              break;
            case "missing":
              runInAction(() => {
                outputBox.set(["value", undefined]);
              });
              break;
            case "unauthorized":
              runInAction(() => {
                outputBox.set(["error", new UnauthorizedError()]);
              });
              break;
            case "value":
              const value = variableAtom.value[1];
              runInAction(() => {
                outputBox.set(["value", value]);
              });
              break;
          }
        }),
      [variableAtom]
    );

    const [isEditingKey, setIsEditingKey] = useState(false);
    const viewMode = activePaneState.viewMode.get();

    return (
      <>
        <PaneHeaderBar
          paneId={paneId}
          paneComputed={paneComputed}
          isMaximized={isMaximized}
        >
          <div className="grow" />
          {!isMaximized ? (
            <PaneTransformButton paneId={paneId} outputIndex={0} />
          ) : null}
        </PaneHeaderBar>
        <PaneBody viewMode={viewMode} paneComputed={paneComputed}>
          <div className="h-8 bg-neutral-50 shrink-0 border-b border-neutral-100">
            {isEditingKey || paneKey === undefined ? (
              <PaneVariableKeyInput
                paneId={paneId}
                paneKey={paneKey}
                onClose={() => setIsEditingKey(false)}
                focusOnMount={focusOnMount || isEditingKey}
              />
            ) : (
              <div className="flex items-center h-full overflow-hidden pr-2">
                <div className="font-mono-natto grow overflow-hidden break-all line-clamp-2 text-ellipsis max-h-8 px-2">
                  {paneKey}
                </div>
                <button
                  onClick={() => {
                    setIsEditingKey(true);
                  }}
                  className="flex shrink-0 text-neutral-400 hover:text-neutral-600 p-1 transition"
                >
                  <FeatherIcon icon="edit" size="sm" />
                </button>
              </div>
            )}
          </div>
          <div className="grow overflow-auto px-2 pt-2">
            {output[0] === "value" ? (
              output[1] !== undefined ? (
                <SecretValue value={output[1]} />
              ) : (
                <div>
                  <button
                    onClick={() => {
                      ParentComlinkAPI.showEnvironmentDialog(paneKey);
                    }}
                    className="bg-neutral-100 text-neutral-500 hover:bg-primary-500 hover:text-neutral-0 rounded-sm px-2 py-1.5 font-medium transition"
                  >
                    define {paneKey}
                  </button>
                </div>
              )
            ) : output[0] === "error" ? (
              output[1] instanceof UnauthorizedError ? (
                <div>
                  <button
                    onClick={() => {
                      ParentComlinkAPI.grantEnvironment();
                    }}
                    className="bg-neutral-100 text-neutral-500 hover:bg-primary-500 hover:text-neutral-0 rounded-sm px-2 py-1.5 font-medium transition"
                  >
                    grant environment access
                  </button>
                </div>
              ) : (
                <div className="text-red-400">{output[1].toString()}</div>
              )
            ) : null}
          </div>
        </PaneBody>
      </>
    );
  }
);
